import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {useToasts} from "react-toast-notifications"
import {useAccessToken, useIsUnitUser, useUserType} from "../../services/auth"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import React, {useEffect, useMemo} from "react"
import {useQueryState} from "use-location-state"
import {usePaging} from "../../hooks/usePaging"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {Filter} from "../Filter/Filter"
import {startCase} from "lodash"
import {ErrorDocument} from "../../resources/common"
import moment from "moment"
import {findIncomingChecks, IncomingCheckStatus} from "../../resources/incomingCheck"
import SearchBox from "../SearchBox/SearchBox"
import {Cents} from "../../resources/transaction"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {findOrgs, Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {Bank, findBanks} from "../../resources/bank"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {downloadFile} from "../../utilities/file"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {AllowedIncomingChecksColumns} from "./IncomingCheck.types"
import {IncomingChecksTable} from "./IncomingCheckTable"

interface IncomingChecksProps {
	limit?: number
	includedColumns: AllowedIncomingChecksColumns[]
	enableStatusFilter?: boolean
	title?: string
	fullHeight?: boolean
	stickyAction?: boolean
	enablePaging?: boolean
	enableBankFilter?: boolean
	enableOrgFilter?: boolean
	enableAmountFilter?: boolean
	enableDateFilter?: boolean
	enableExport?: boolean
	enableSearch?: boolean
}

export default function IncomingChecks({
	limit = 25,
	includedColumns,
	stickyAction = false,
	fullHeight = false,
	enablePaging = true,
	enableStatusFilter = true,
	enableBankFilter = true,
	enableOrgFilter = true,
	enableAmountFilter = true,
	enableDateFilter = true,
	enableSearch = true,
	enableExport = true,
	title,
}: IncomingChecksProps) {
	const prefix = "incoming-check"
	const accessToken = useAccessToken()
	const userType = useUserType()
	const {addToast} = useToasts()
	const [refreshToken, refresh] = useRefreshToken()
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [filteredBanks, setFilteredBanks] = useQueryState<string[]>(`${prefix}-filter[banks]`, [])
	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search by Incoming Check Id", 500, onSearch)
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")

	const [statuses, setStatuses] = useQueryState(
		`${prefix}-filter[status]`,
		enableStatusFilter ? [IncomingCheckStatus.PendingReview] : []
	)
	const statusesOptions = new Map(Object.values(IncomingCheckStatus).map((v) => [v, startCase(v)]))
	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findIncomingChecks(
				accessToken,
				offset,
				query,
				limit,
				statuses,
				filteredOrgs,
				filteredBanks,
				since,
				until,
				fromAmount,
				toAmount,
				"json"
			),
		(x) => x.data.length,
		[
			refreshToken,
			since,
			until,
			query,
			[...statuses, ...filteredOrgs, ...filteredBanks].join(","),
			fromAmount,
			toAmount,
		],
		`${prefix}-`,
		true
	)

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const banks = useIsUnitUser()
		? useAsyncResult(() => findBanks(accessToken, 0, 10000), [])
		: AsyncResult.pending<Bank[], ErrorDocument>()
	const bankFilter = banks.match(
		() => null,
		(banks) => (
			<Filter
				title="Banks"
				isSearchable={true}
				setStatuses={setFilteredBanks}
				statuses={filteredBanks}
				onFilterFunc={() => reset(limit)}
				options={
					new Map<string, string>(
						banks
							.sort((a, b) => moment(b.attributes.createdAt).diff(moment(a.attributes.createdAt)))
							.map((bank) => [bank.id, bank.attributes.name])
					)
				}
			/>
		),
		(_) => null
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={setFromAmount}
			to={toAmount}
			setTo={setToAmount}
		/>
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	async function exportIncomingChecks() {
		const response = await findIncomingChecks(
			accessToken,
			0,
			query,
			null,
			statuses,
			filteredOrgs,
			filteredBanks,
			since,
			until,
			fromAmount,
			toAmount,
			"csv"
		)

		response.match(
			(cd) => downloadFile("IncomingChecks.csv", cd, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)
		return true
	}

	return (
		<DataTableCard className={"incoming-checks-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={enableSearch ? searchBox : null}
				dateTimePicker={enableDateFilter ? dateTime : null}
				filters={[
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableBankFilter ? [bankFilter] : []),
					...(enableStatusFilter ? [statusFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
				]}
				title={title}
				exportFunc={enableExport ? exportIncomingChecks : undefined}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: incomingChecks}) => {
					return (
						<IncomingChecksTable
							fullHeight={fullHeight}
							incomingChecks={incomingChecks.data}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							include={incomingChecks.included}
							includedColumns={includedColumns}
							isUsingPaging={enablePaging}
							stickyAction={stickyAction}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
